import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CreditCard, Camera, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { Html5Qrcode } from "html5-qrcode";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhoneInput from "@/components/PhoneInput";

function CardScanner({ onScan, label }) {
  const [manual, setManual] = useState("");
  const [scanning, setScanning] = useState(false);
  const ref = useRef(null);

  const stop = async () => { try { await ref.current?.stop(); ref.current?.clear(); } catch { /* stopped */ } ref.current = null; setScanning(false); };
  useEffect(() => () => { stop(); }, []);
  const start = () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const s = new Html5Qrcode("card-reader"); ref.current = s;
        await s.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
          async (d) => { await stop(); onScan(d); }, () => {});
      } catch { toast.error("Camera unavailable — paste the code"); setScanning(false); }
    }, 100);
  };

  return (
    <div className="space-y-3">
      {scanning ? (
        <div className="card-surface p-4"><div id="card-reader" className="rounded-xl overflow-hidden" /><Button data-testid="card-camera-stop" variant="outline" onClick={stop} className="w-full mt-3 rounded-xl border-black/10">Stop camera</Button></div>
      ) : (
        <Button data-testid="card-camera" onClick={start} className="w-full h-14 rounded-2xl bg-primary/15 text-primary hover:bg-primary/20 font-semibold border border-primary/20"><Camera size={22} className="mr-2" /> Scan the card</Button>
      )}
      <div className="card-surface p-4 space-y-2">
        <p className="text-xs text-muted-foreground uppercase tracking-wider font-mono">{label}</p>
        <Input data-testid="card-token" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="OKC.…" className="bg-muted/40 border-black/10 h-11 rounded-xl font-mono text-xs" />
        <Button data-testid="card-token-submit" onClick={() => onScan(manual)} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Continue</Button>
      </div>
    </div>
  );
}

export default function CardFlow() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "replace" ? "replace" : "register");
  const [busy, setBusy] = useState(false);
  const err = (e) => toast.error(apiError(e.response?.data?.detail) || e.message);

  // register state
  const [token, setToken] = useState("");
  const [cardRef, setCardRef] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("+255");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [done, setDone] = useState(null);

  // replace state
  const [rPhone, setRPhone] = useState("+255");
  const [rOtp, setROtp] = useState("");
  const [rOtpSent, setROtpSent] = useState(false);
  const [rToken, setRToken] = useState("");

  const reset = () => { setToken(""); setCardRef(""); setName(""); setPhone(""); setOtp(""); setOtpSent(false); setDone(null); setRPhone(""); setROtp(""); setROtpSent(false); setRToken(""); };

  const lookup = async (t) => {
    if (!t) return toast.error("Scan or paste the card");
    setBusy(true);
    try { const { data } = await api.post("/staff/card/lookup", { token: t.trim() }); setToken(t.trim()); setCardRef(data.card_reference); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  const sendOtp = async () => {
    if (!phone) return toast.error("Enter the customer's mobile number");
    setBusy(true);
    try { const { data } = await api.post("/staff/card/otp", { phone }); toast.success("OTP sent to the customer's phone"); setOtpSent(true); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/staff/card/activate", { token, phone, otp, full_name: name });
      setDone(data);
      toast.success(data.new_account ? "Account created & card activated" : "Card linked to existing account");
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const rSendOtp = async () => {
    if (!rPhone) return toast.error("Enter the customer's mobile number");
    setBusy(true);
    try { const { data } = await api.post("/staff/card/otp", { phone: rPhone }); toast.success("OTP sent to the customer's phone"); setROtpSent(true); }
    catch (e) { err(e); } finally { setBusy(false); }
  };

  const replace = async (newTok) => {
    setBusy(true);
    try {
      const { data } = await api.post("/staff/card/replace", { phone: rPhone, otp: rOtp, new_token: newTok.trim() });
      setDone(data);
      toast.success(`Card replaced (${data.replaced_count} old card revoked)`);
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="space-y-5 pt-6 text-center">
        <div className="h-16 w-16 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto"><CheckCircle size={34} weight="fill" className="text-primary" /></div>
        <div>
          <h1 className="font-display font-bold text-xl" data-testid="card-done-name">{done.customer_name}</h1>
          <p className="text-sm text-muted-foreground">OneKite ID <span className="font-mono">{done.onekite_id_formatted}</span></p>
          <p className="text-sm text-primary mt-1">Card {done.card_reference} is now active</p>
        </div>
        <div className="card-surface p-4 text-sm text-muted-foreground">Hand the physical card to the customer. They can use it at any OneKite business. To use the app later, they tap “Claim your account”.</div>
        <div className="flex gap-3">
          <Button data-testid="card-another" onClick={() => { reset(); }} className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Another card</Button>
          <Button data-testid="card-done" variant="outline" onClick={() => nav("/staff")} className="flex-1 h-11 rounded-xl border-black/10">Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <button onClick={() => nav("/staff")} data-testid="card-home" className="text-muted-foreground flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Home</button>
      <div>
        <h1 className="font-display font-bold text-2xl flex items-center gap-2"><CreditCard size={24} className="text-primary" /> OneKite Cards</h1>
        <p className="text-sm text-muted-foreground">Register a customer onto a card or replace a lost one.</p>
      </div>

      <div className="grid grid-cols-2 gap-2 bg-muted/40 rounded-full p-1">
        <button data-testid="mode-register" onClick={() => { setMode("register"); reset(); }} className={`h-9 rounded-full text-sm font-medium ${mode === "register" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Register / Activate</button>
        <button data-testid="mode-replace" onClick={() => { setMode("replace"); reset(); }} className={`h-9 rounded-full text-sm font-medium ${mode === "replace" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Replace card</button>
      </div>

      {mode === "register" && (
        <div className="space-y-4">
          {!cardRef ? (
            <CardScanner label="Scan an unused OneKite card" onScan={lookup} />
          ) : (
            <>
              <div className="card-surface p-3 text-sm flex items-center justify-between">
                <span>Card <span className="font-mono font-semibold">{cardRef}</span></span>
                <button data-testid="card-change" onClick={() => { setCardRef(""); setToken(""); }} className="text-xs text-primary">Change</button>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Customer name</Label>
                <Input data-testid="reg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Starla Matiku" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Mobile number</Label>
                <div className="mt-1.5"><PhoneInput testid="reg-phone" value={phone} onChange={setPhone} /></div>
              </div>
              {!otpSent ? (
                <Button data-testid="reg-send-otp" onClick={sendOtp} disabled={busy || !name || !phone} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Send OTP to customer</Button>
              ) : (
                <>
                  <div>
                    <Label className="text-xs text-muted-foreground">OTP from customer</Label>
                    <Input data-testid="reg-otp" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
                  </div>
                  <Button data-testid="reg-activate" onClick={activate} disabled={busy || !otp} className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"><CheckCircle size={18} weight="fill" className="mr-2" /> Verify & activate card</Button>
                </>
              )}
            </>
          )}
        </div>
      )}

      {mode === "replace" && (
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">Customer mobile number</Label>
            <div className="mt-1.5"><PhoneInput testid="rep-phone" value={rPhone} onChange={setRPhone} /></div>
          </div>
          {!rOtpSent ? (
            <Button data-testid="rep-send-otp" onClick={rSendOtp} disabled={busy || !rPhone} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Send OTP to customer</Button>
          ) : (
            <>
              <div>
                <Label className="text-xs text-muted-foreground">OTP from customer</Label>
                <Input data-testid="rep-otp" value={rOtp} onChange={(e) => setROtp(e.target.value)} placeholder="6-digit code" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><ArrowsClockwise size={14} /> Scan the new unused card — the old one will be revoked.</p>
              <CardScanner label="Scan the NEW unused card" onScan={(t) => { setRToken(t); replace(t); }} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
